import React, { useCallback, useEffect, useRef, useState } from "react";

// On-device console login gate. Mounted at the root in single-edge mode, so it
// wraps every surface — landing chooser, Console and Ada alike — and renders
// children only once the operator has authenticated against the edge's
// /console/api/auth endpoints. The session lives in an HttpOnly cookie set by
// the edge, so this component never touches the token directly — it only asks
// the edge whether the current request is authenticated.

const API = "/console/api";
const MIN_LEN = 8;

export default function LoginGate({ children }) {
  const [status, setStatus] = useState("checking"); // checking | setup | login | recovery | error | authed
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pairingToken, setPairingToken] = useState("");
  const [pairingRequired, setPairingRequired] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  const checkStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/auth/status`, {
        credentials: "same-origin",
        cache: "no-store",
      });
      if (res.ok) {
        const data = await res.json();
        setPairingRequired(data.pairing_required === true);
        if (data.recovery_required) setStatus("recovery");
        else if (data.authenticated) setStatus("authed");
        else if (data.configured === false) setStatus("setup");
        else setStatus("login");
        return;
      }
      setStatus("error");
    } catch {
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  useEffect(() => {
    if ((status === "login" || status === "setup") && inputRef.current) {
      inputRef.current.focus();
    }
  }, [status]);

  const onLogin = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        setPassword("");
        setStatus("authed");
        return;
      }
      if (res.status === 409) {
        await checkStatus();
        setError("");
      } else if (res.status === 401) {
        setError("Incorrect password.");
      } else {
        const data = await res.json();
        if (data.recovery_required) setStatus("recovery");
        else setError(res.status === 429 ? "Too many attempts. Wait a moment and try again." : `Sign-in failed (${res.status}).`);
      }
    } catch {
      setError("Could not reach the device. Check your connection.");
    } finally {
      setSubmitting(false);
    }
  };

  const onSetup = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (password.length < MIN_LEN) {
      setError(`Password must be at least ${MIN_LEN} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (pairingRequired && !pairingToken.trim()) {
      setError("Enter the pairing code provided locally with this device.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/setup`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password, ...(pairingRequired ? { pairing_token: pairingToken.trim() } : {}) }),
      });
      if (res.ok) {
        setPassword("");
        setConfirm("");
        setStatus("authed");
        return;
      }
      if (res.status === 409) {
        await checkStatus();
        setPassword("");
        setConfirm("");
        setError("A password has already been set for this device.");
      } else {
        const data = await res.json();
        if (data.recovery_required) setStatus("recovery");
        else setError(res.status === 429 ? "Too many attempts. Wait a moment and try again." : data.error || `Could not set password (${res.status}).`);
      }
    } catch {
      setError("Could not reach the device. Check your connection.");
    } finally {
      setPairingToken("");
      setSubmitting(false);
    }
  };

  if (status === "authed") return children;

  const isSetup = status === "setup";

  return (
    <div style={S.page}>
      <style>{css}</style>
      <div style={S.card}>
        <div style={S.brandRow}>
          <div style={S.logoDot} />
          <span style={S.brand}>Weavian</span>
        </div>
        <h1 style={S.title}>Edge Console</h1>
        <p style={S.subtitle}>
          {status === "checking"
            ? "Connecting to this device…"
            : status === "recovery"
            ? "Local recovery required."
            : status === "error"
            ? "Could not reach this device. Check your connection and try again."
            : isSetup
            ? "First-time setup — create a password to secure this device."
            : "Sign in to manage this device."}
        </p>

        {status === "checking" ? (
          <div style={S.checking}>
            <span className="lg-spinner" />
            <span>Connecting…</span>
          </div>
        ) : status === "recovery" || status === "error" ? (
          <div style={S.form}>
            {status === "recovery" && <p role="alert" style={S.subtitle}>Access is locked because this device's saved credentials need recovery. Ask the device administrator to recover access locally, then try again.</p>}
            <button type="button" style={S.button} onClick={checkStatus}>Try again</button>
          </div>
        ) : isSetup ? (
          <form onSubmit={onSetup} style={S.form}>
            <label style={S.label} htmlFor="lg-pass">
              New password
            </label>
            <input
              id="lg-pass"
              ref={inputRef}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={S.input}
              placeholder={`At least ${MIN_LEN} characters`}
            />
            <label style={S.label} htmlFor="lg-confirm">
              Confirm password
            </label>
            <input
              id="lg-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              style={S.input}
              placeholder="Re-enter password"
            />
            {pairingRequired && <>
              <label style={S.label} htmlFor="lg-pairing">Pairing code</label>
              <input
                id="lg-pairing"
                type="password"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                value={pairingToken}
                onChange={(e) => setPairingToken(e.target.value)}
                style={S.input}
                aria-describedby="lg-pairing-help"
              />
              <p id="lg-pairing-help" style={S.subtitle}>Use the code provided locally with this device. Internet access is not required.</p>
            </>}
            {error && <div style={S.error}>{error}</div>}
            <button
              type="submit"
              style={S.button}
              disabled={submitting || !password || !confirm || (pairingRequired && !pairingToken.trim())}
            >
              {submitting ? "Saving…" : "Set password & continue"}
            </button>
          </form>
        ) : (
          <form onSubmit={onLogin} style={S.form}>
            <label style={S.label} htmlFor="lg-pass">
              Password
            </label>
            <input
              id="lg-pass"
              ref={inputRef}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={S.input}
              placeholder="••••••••••"
            />
            {error && <div style={S.error}>{error}</div>}
            <button type="submit" style={S.button} disabled={submitting || !password}>
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
        )}
        <div style={S.footer}>NotABox · single-device console</div>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      "radial-gradient(1200px 600px at 50% -10%, #16223a 0%, #0b1020 55%, #070a14 100%)",
    padding: 24,
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
  card: {
    width: "100%",
    maxWidth: 380,
    background: "rgba(17, 24, 39, 0.72)",
    border: "1px solid rgba(148, 163, 184, 0.16)",
    borderRadius: 18,
    padding: "34px 30px 22px",
    boxShadow: "0 24px 60px rgba(0,0,0,0.45)",
    backdropFilter: "blur(14px)",
  },
  brandRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 22 },
  logoDot: {
    width: 14,
    height: 14,
    borderRadius: 5,
    background: "linear-gradient(135deg, #38bdf8, #6366f1)",
    boxShadow: "0 0 18px rgba(56,189,248,0.6)",
  },
  brand: { color: "#e2e8f0", fontWeight: 700, letterSpacing: 0.4, fontSize: 15 },
  title: { color: "#f8fafc", fontSize: 24, fontWeight: 700, margin: "0 0 6px" },
  subtitle: { color: "#94a3b8", fontSize: 14, margin: "0 0 22px" },
  form: { display: "flex", flexDirection: "column", gap: 8 },
  label: {
    color: "#cbd5e1",
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  input: {
    background: "rgba(15, 23, 42, 0.8)",
    border: "1px solid rgba(148, 163, 184, 0.22)",
    borderRadius: 10,
    padding: "12px 14px",
    color: "#f1f5f9",
    fontSize: 15,
    outline: "none",
  },
  error: {
    color: "#fca5a5",
    fontSize: 13,
    background: "rgba(239, 68, 68, 0.08)",
    border: "1px solid rgba(239, 68, 68, 0.25)",
    borderRadius: 8,
    padding: "8px 10px",
  },
  button: {
    marginTop: 8,
    background: "linear-gradient(135deg, #3b82f6, #6366f1)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "12px 14px",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  checking: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    color: "#94a3b8",
    fontSize: 14,
    padding: "12px 0",
  },
  footer: {
    marginTop: 22,
    textAlign: "center",
    color: "#475569",
    fontSize: 12,
  },
};

const css = `
.lg-spinner {
  width: 16px; height: 16px; border-radius: 50%;
  border: 2px solid rgba(148,163,184,0.3);
  border-top-color: #38bdf8;
  display: inline-block;
  animation: lg-spin 0.8s linear infinite;
}
@keyframes lg-spin { to { transform: rotate(360deg); } }
`;
